import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Box, Divider, IconButton, Paper, Stack, Tooltip, useTheme } from "@mui/material";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, { useEffect } from "react";

type Props = {
  value: string;
  onChange(value: string): void;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
};

function ToolbarButton({
  title,
  active,
  disabled,
  onClick,
  children,
}: {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          color={active ? "primary" : "default"}
          aria-label={title}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function RichTextEditor({
  value,
  onChange,
  disabled = false,
  placeholder = "Edit email body…",
  minHeight = 200,
}: Props) {
  const theme = useTheme();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getHTML());
    },
    editorProps: {
      attributes: {
        "aria-label": "Email body",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    const currentHtml = editor.getHTML();
    if (value !== currentHtml) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  const handleSetLink = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Enter link URL", previousUrl ?? "https://");

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", bgcolor: "background.paper" }}>
      <Stack
        direction="row"
        spacing={0.25}
        alignItems="center"
        sx={{
          px: 0.5,
          py: 0.25,
          flexWrap: "wrap",
          bgcolor: "action.hover",
        }}
      >
        <ToolbarButton
          title="Bold"
          active={editor?.isActive("bold")}
          disabled={disabled || !editor}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor?.isActive("italic")}
          disabled={disabled || !editor}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          active={editor?.isActive("underline")}
          disabled={disabled || !editor}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        >
          <FormatUnderlinedIcon fontSize="small" />
        </ToolbarButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton
          title="Add link"
          active={editor?.isActive("link")}
          disabled={disabled || !editor}
          onClick={handleSetLink}
        >
          <LinkIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Remove link"
          disabled={disabled || !editor || !editor.isActive("link")}
          onClick={() => editor?.chain().focus().unsetLink().run()}
        >
          <LinkOffIcon fontSize="small" />
        </ToolbarButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton
          title="Bullet list"
          active={editor?.isActive("bulletList")}
          disabled={disabled || !editor}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToolbarButton>
      </Stack>
      <Divider />
      <Box
        sx={{
          position: "relative",
          "& .ProseMirror": {
            outline: "none",
            minHeight,
            maxHeight: 320,
            overflowY: "auto",
            px: 2,
            py: 1.5,
            fontSize: theme.typography.body1.fontSize,
            lineHeight: theme.typography.body1.lineHeight,
            color: "text.primary",
            "& p": { m: 0, mb: 1 },
            "& p:last-child": { mb: 0 },
            "& ul": { m: 0, mb: 1, pl: 3 },
            "& a": {
              color: "primary.main",
              textDecoration: "underline",
              cursor: "pointer",
            },
            "&:empty::before": {
              content: `"${placeholder}"`,
              color: "text.disabled",
              pointerEvents: "none",
              float: "left",
              height: 0,
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
}
